import { useEffect, useState } from 'react';
import { Play, Loader2, AlertCircle, CircleCheck, CircleSlash, Sparkles, Award, ArrowRight, Layers, ChevronDown, Wand2, FormInput } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AdminAPI } from '@/lib/admin-api';
import { formatINR } from '@/lib/format';
import { ConfidenceBadge } from '@/components/admin/RuleBuilder';
import { SimulatorGuidedForm } from '@/components/admin/SimulatorGuidedForm';
import { cn } from '@/lib/utils';

const EXAMPLES = [
  { label: 'Swiggy 2k',        amount: 2000,    merchant: 'swiggy',  mcc: '5814', category: 'Dining',    mode: 'online' },
  { label: 'Dining 20L',       amount: 2000000, merchant: 'swiggy',  mcc: '5814', category: 'Dining',    mode: 'online' },
  { label: 'Fuel 3k',          amount: 3000,    merchant: 'hpcl',    mcc: '5541', category: 'Fuel',      mode: 'offline' },
  { label: 'Amazon 5k',        amount: 5000,    merchant: 'amazon',  mcc: '5310', category: 'Ecommerce', mode: 'online' },
  { label: 'BA intl 80k',      amount: 80000,   merchant: 'british airways', mcc: '3008', category: 'Flights', mode: 'online', international: true },
];

const PORTION_META = {
  primary:   { tone: 'border-primary/30 bg-primary/5 text-primary',                            label: 'Primary',     Icon: ArrowRight },
  spillover: { tone: 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300',  label: 'Spillover',   Icon: Layers },
  milestone: { tone: 'border-accent/30 bg-accent/5 text-accent-foreground',                    label: 'Milestone',   Icon: Award },
};

export const RuleSimulator = ({ cardId, versionId, versionStatus, cardRules = [] }) => {
  const [form, setForm] = useState({
    amount: 2000, merchant: 'swiggy', mcc: '5814', category: 'Dining',
    mode: 'online', international: false,
    period_used_inr: 0, period_used_points: 0, milestone_progress_inr: 0,
  });
  // Phase 23.F — Transaction contexts (portal_channel / subcategory).
  // Threaded through to the simulator backend, which already accepts them
  // via SimulatorRequest.contexts. Lives alongside `form` rather than
  // inside it because contexts are typed as { string -> string[] } and
  // the existing flat form is { string -> primitive }.
  const [contexts, setContexts] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [autoRun, setAutoRun] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Default to Guided mode; admins can flip to the legacy flat form.
  const [mode, setMode] = useState('guided');

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const out = await AdminAPI.simulate({
        card_id: cardId,
        version_id: versionId,
        amount: Number(form.amount) || 0,
        merchant: form.merchant || undefined,
        mcc: form.mcc || undefined,
        category: form.category || undefined,
        mode: form.mode,
        international: !!form.international,
        period_used_inr: Number(form.period_used_inr) || 0,
        period_used_points: Number(form.period_used_points) || 0,
        milestone_progress_inr: Number(form.milestone_progress_inr) || 0,
        // Phase 23.F — pass the runtime contexts through to the engine.
        contexts: Object.keys(contexts).some((k) => (contexts[k] || []).length)
          ? contexts
          : undefined,
      });
      setResult(out);
    } catch (e) {
      setError(e.message || 'Simulation failed');
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!autoRun) return;
    const t = setTimeout(run, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form, contexts, autoRun, versionId]);

  const apply = (ex) => setForm((f) => ({ ...f, international: false, ...ex }));

  const r = result?.result;
  const portions = r?.portions || [];

  return (
    <div data-testid="admin-rule-simulator-pane" className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Simulator</div>
          <Badge variant="outline" className="text-[10px] gap-1"><Sparkles className="h-3 w-3" /> Deterministic</Badge>
          {versionStatus && <Badge variant="outline" className="text-[10px]">{versionStatus}</Badge>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Phase 23.F — mode toggle: guided question flow vs. flat form */}
          <div
            className="inline-flex rounded-md border border-border bg-background overflow-hidden text-[10px] uppercase tracking-wider"
            data-testid="admin-rule-simulator-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setMode('guided')}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-1 transition-colors',
                mode === 'guided' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/30',
              )}
              data-testid="admin-rule-simulator-mode-guided"
            >
              <Wand2 className="h-3 w-3" /> Guided
            </button>
            <button
              type="button"
              onClick={() => setMode('flat')}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-1 border-l border-border transition-colors',
                mode === 'flat' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/30',
              )}
              data-testid="admin-rule-simulator-mode-flat"
            >
              <FormInput className="h-3 w-3" /> Advanced
            </button>
          </div>
          <label className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer">
            <Switch checked={autoRun} onCheckedChange={setAutoRun} data-testid="admin-rule-simulator-auto-run" />
            <span className="uppercase tracking-wider">Auto-run</span>
          </label>
        </div>
      </div>

      {/* Form */}
      <Card className="p-3 space-y-2.5">
        {mode === 'guided' ? (
          <SimulatorGuidedForm
            cardRules={cardRules}
            form={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            contexts={contexts}
            onContextsChange={setContexts}
          />
        ) : (
          <>
        {/* Row 1: amount + mode */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="Amount (₹)">
            <Input type="number" inputMode="numeric" value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              className="h-8 font-mono-num text-xs" data-testid="admin-rule-simulator-amount" />
          </Field>
          <Field label="Mode">
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="admin-rule-simulator-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Row 2: merchant + mcc */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="Merchant">
            <Input value={form.merchant} onChange={(e) => setForm((f) => ({ ...f, merchant: e.target.value }))}
              className="h-8 text-xs" data-testid="admin-rule-simulator-merchant" />
          </Field>
          <Field label="MCC">
            <Input value={form.mcc} onChange={(e) => setForm((f) => ({ ...f, mcc: e.target.value }))}
              className="h-8 font-mono-num text-xs" data-testid="admin-rule-simulator-mcc" />
          </Field>
        </div>

        {/* Row 3: category */}
        <Field label="Category">
          <Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="h-8 text-xs" data-testid="admin-rule-simulator-category" />
        </Field>

        {/* Advanced (collapsed by default) */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground pt-1.5 border-t border-border transition-colors"
          data-testid="admin-rule-simulator-advanced-toggle"
        >
          <span>Advanced · period usage, milestone progress, international</span>
          <ChevronDown className={cn('h-3 w-3 transition-transform', showAdvanced && 'rotate-180')} />
        </button>
        {showAdvanced && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Period used (₹)" hint="Already-spent under this rule">
              <Input type="number" value={form.period_used_inr}
                onChange={(e) => setForm((f) => ({ ...f, period_used_inr: e.target.value }))}
                className="h-8 font-mono-num text-xs" data-testid="admin-rule-simulator-period-used" />
            </Field>
            <Field label="Points already earned" hint="For point-cap simulation">
              <Input type="number" value={form.period_used_points}
                onChange={(e) => setForm((f) => ({ ...f, period_used_points: e.target.value }))}
                className="h-8 font-mono-num text-xs" data-testid="admin-rule-simulator-period-used-points" />
            </Field>
            <Field label="Milestone progress (₹)" hint="Spend so far toward annual milestone">
              <Input type="number" value={form.milestone_progress_inr}
                onChange={(e) => setForm((f) => ({ ...f, milestone_progress_inr: e.target.value }))}
                className="h-8 font-mono-num text-xs" data-testid="admin-rule-simulator-milestone-progress" />
            </Field>
            <Field label="International">
              <div className="h-8 flex items-center">
                <Switch checked={form.international} onCheckedChange={(v) => setForm((f) => ({ ...f, international: v }))} data-testid="admin-rule-simulator-intl" />
                <span className="ml-2 text-[10px] text-muted-foreground">{form.international ? 'Yes' : 'No'}</span>
              </div>
            </Field>
          </div>
        )}
          </>
        )}

        {/* Run + examples (shared by both modes) */}
        <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border">
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                onClick={() => apply(ex)}
                className="text-[10px] rounded-full border border-border bg-[hsl(var(--surface-2))] hover:bg-accent/10 hover:border-accent/30 px-2 py-0.5"
                data-testid={`admin-rule-simulator-example-${ex.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {ex.label}
              </button>
            ))}
          </div>
          <Button size="sm" className="gap-1.5 shrink-0" onClick={run} disabled={running} data-testid="admin-rule-simulator-run">
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run
          </Button>
        </div>
      </Card>

      {/* Result */}
      <div className="mt-3" data-testid="admin-rule-simulator-result">
        {error && (
          <Card className="p-2.5 border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300 text-xs flex gap-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5" /><span>{error}</span>
          </Card>
        )}
        {!result && !error && (
          <Card className="p-6 text-center text-xs text-muted-foreground border-dashed" data-testid="admin-rule-simulator-empty">
            Run a simulation to see which rule fires and what the deterministic engine returns.
          </Card>
        )}
        {result && r && (
          <Card className="p-3 space-y-3">
            {/* Status + meta */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {r.excluded ? (
                <Badge className="gap-1.5 bg-rose-500/10 text-rose-600 border-rose-500/30"><CircleSlash className="h-3 w-3" /> Excluded</Badge>
              ) : (
                <Badge className="gap-1.5 bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><CircleCheck className="h-3 w-3" /> Earned</Badge>
              )}
              <div className="text-[10px] text-muted-foreground">
                v{result.version.version} · confidence {((r.confidence || 0) * 100).toFixed(0)}% · {result.all_rules_evaluated} rule{result.all_rules_evaluated === 1 ? '' : 's'} evaluated
              </div>
            </div>

            {/* Totals — 3 large stats */}
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Reward (raw)" value={`${(r.reward_raw || 0).toFixed(2)} ${r.reward_currency || ''}`} />
              <Stat label="₹ value (total)" value={formatINR(r.reward_value_inr || 0)} accent />
              <Stat label="Effective rate" value={`${(r.effective_rate_pct || 0).toFixed(2)}%`} />
            </div>

            {/* Portion breakdown */}
            {portions.length > 0 && (
              <div className="space-y-1.5" data-testid="admin-rule-simulator-portions">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Breakdown</div>
                {portions.map((p, i) => {
                  const meta = PORTION_META[p.kind] || PORTION_META.primary;
                  const Icon = meta.Icon;
                  return (
                    <div
                      key={i}
                      className={cn('rounded-md border px-2 py-1.5 text-[11px]', meta.tone)}
                      data-testid={`admin-rule-simulator-portion-${p.kind}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Icon className="h-3 w-3 shrink-0" />
                          <span className="text-[9px] uppercase tracking-wider opacity-70">{meta.label}</span>
                          <span className="font-medium truncate">{p.rule_label}</span>
                        </div>
                        <div className="font-mono-num font-semibold">
                          +{(p.reward_raw || 0).toFixed(2)} {p.currency || r.reward_currency} <span className="opacity-70">· {formatINR(p.reward_value_inr || 0)}</span>
                        </div>
                      </div>
                      {p.note && <div className="text-[10px] opacity-80 mt-0.5 ml-4">{p.note}</div>}
                      {p.kind !== 'milestone' && p.eligible_amount_inr > 0 && (
                        <div className="text-[10px] opacity-60 mt-0.5 ml-4 font-mono-num">on ₹{Number(p.eligible_amount_inr).toLocaleString('en-IN')} eligible spend</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Fired rule + provenance + why_not */}
            <div className="text-xs space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fired rule</div>
              <div className="flex items-center gap-2 flex-wrap">
                {result.fired_rule ? (
                  <>
                    <b>{result.fired_rule.label}</b>
                    <span className="text-muted-foreground">(priority {result.fired_rule.priority})</span>
                    {result.fired_rule.provenance && <ConfidenceBadge provenance={result.fired_rule.provenance} />}
                  </>
                ) : '— no matching rule'}
              </div>
              {result.fired_rule?.provenance?.observation_notes && (
                <div className="text-[10px] text-muted-foreground italic">{result.fired_rule.provenance.observation_notes}</div>
              )}
              {r.cap_remaining_inr != null && r.cap_remaining_inr > 0 && (
                <div className="text-[10px] text-amber-600">Cap remaining: ₹{Number(r.cap_remaining_inr).toLocaleString('en-IN')}</div>
              )}
              {r.why_not && <div className="text-[10px] text-muted-foreground">{r.why_not}</div>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

// ----- Layout helpers -----------------------------------------------------
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    {hint && <span className="text-[9px] text-muted-foreground/70 ml-1">· {hint}</span>}
    <div className="mt-1">{children}</div>
  </label>
);

const Stat = ({ label, value, accent }) => (
  <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-2 py-1.5">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={cn('font-mono-num text-sm font-semibold', accent && 'text-primary')}>{value}</div>
  </div>
);
